<template>
  <div class="promo-modal">
    <button
      aria-label="Close"
      class="promo-modal__close"
      @click="onClose"
    >
      <gl-icon
        gray
        :height="16"
        name="I-Close"
        :width="16"
      />
    </button>

    <div class="promo-modal__img-wrap">
      <img
        alt="Subscriptions Cards"
        :src="subscriptionsCards"
      >
    </div>

    <h2 class="promo-modal__title">
      Goodbye Lookups, Hello Subscriptions!
    </h2>
    <p class="promo-modal__text">
      We’re switching to a subscription model.
    </p>
    <p class="promo-modal__text">
      As a thank you for being with us, we’re giving you
      <span
        class="promo-modal__highlight"
      >1 month of our Standard Plan for FREE</span>
      to make the switch easier.
    </p>

    <p class="promo-modal__label">
      Simply use this promo code at Stripe checkout:
    </p>
    <div class="promo-modal__code-row">
      <span class="promo-modal__code">{{ promoCodeDisplay }}</span>
      <button
        aria-label="Copy code"
        class="promo-modal__copy"
        type="button"
        @click="copyCode"
      >
        <gl-icon
          gray
          :height="20"
          name="I-Copy"
          :width="20"
        />
      </button>
    </div>

    <gl-button
      class="promo-modal__action-btn"
      dark
      full
      :loading="claimLoading"
      size="large"
      :title="claimButtonLabel"
      @click="handleClaim"
    />
  </div>
</template>

<script>
// components
import GlIcon from '@/components/gl-icon'
import GlButton from '@/components/gl-button'
// services
import { setPromoDismissed, defaultPromoCode } from '@/services/promo-campaign'
// Vuex
import { mapActions } from 'vuex'
// img
import subscriptionsCards from '@/assets/images/subscriptions/Subscription-Image.png'

export default {
  name: 'PromoCampaignModal',
  components: {
    GlIcon,
    GlButton,
  },
  props: {
    campaign: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      subscriptionsCards,
      claimLoading: false,
    }
  },
  computed: {
    claimButtonLabel() {
      return 'Claim My Free Month'
    },
    promoCodeDisplay() {
      return this.campaign.promoCode || defaultPromoCode
    },
  },
  methods: {
    ...mapActions('payment', ['createCheckout']),
    onClose() {
      setPromoDismissed()
      this.$emit('close')
    },
    copyCode() {
      navigator.clipboard.writeText(this.promoCodeDisplay).then(() => {
        this.$toast.add({
          severity: 'info',
          detail: 'Copied!',
          life: 3000,
        })
      })
    },
    async handleClaim() {
      this.claimLoading = true
      try {
        const returnUrl = window.location.href + (window.location.search ? '&' : '?') + 'payment_success=promo'
        const response = await this.createCheckout({
          planTier: 'standard',
          billingCycle: 'monthly',
          paymentMethod: 'card',
          returnUrl,
          promotionCode: this.campaign?.promoCode || defaultPromoCode,
        })
        const data = response?.data
        if (!response?.success && response?.data?.error) {
          this.$toast?.add?.({
            severity: 'error',
            summary: 'Error',
            detail: response.data.error || 'Checkout failed',
            life: 5000,
          })
          return
        }
        if (data?.redirectUrl) {
          this.$emit('close')
          window.location.href = data.redirectUrl
        }
      } catch (err) {
        const detail =
          err?.response?.data?.error || err?.message || 'Checkout failed'

        this.$toast?.add?.({
          severity: 'error',
          summary: 'Error',
          detail,
          life: 5000,
        })
      } finally {
        this.claimLoading = false
      }
    },
  },
}
</script>

<style scoped>
.promo-modal {
  position: relative;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  max-width: 440px;
  width: 100%;
  padding: 24px;
  border-radius: 24px;
  background: var(--Background-S1, #fff);
}

.promo-modal__close {
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.promo-modal__close:hover {
  background: var(--light-grey-e-f);
}

.promo-modal__img-wrap {
  display: flex;
  justify-content: start;
}
.promo-modal__img-wrap img {
  width: 136px;
  height: 136px;
}

.promo-modal__title {
  font-size: var(--Size-3XL, 24px);
  font-weight: 700;
  line-height: var(--Line-Height-3XL, 32px);
  margin-bottom: 8px;
}

.promo-modal__text {
  font-size: var(--Size-M, 14px);
  font-weight: 500;
  line-height: var(--Line-Height-L, 20px);
  letter-spacing: var(--Letter-Spacing-S, -0.2px);
  margin-bottom: 8px;
  max-width: 350px;
}

.promo-modal__highlight {
  color: var(--Brand-Main, #194bfb);
  font-weight: 600;
}

.promo-modal__label {
  font-size: var(--Size-L, 16px);
  font-weight: 700;
  line-height: var(--Line-Height-XL, 24px);
  margin: 16px 0;
}

.promo-modal__code-row {
  display: flex;
  align-items: center;
  gap: 6px;
  border-radius: 8px;
  border: 1px dashed var(--Basic-500);
  padding: 8px;
  margin-bottom: 32px;
}

.promo-modal__code {
  flex: 1 0 0;
  font-size: var(--Size-M, 14px);
  font-weight: 500;
  line-height: var(--Line-Height-L, 20px);
  letter-spacing: var(--Letter-Spacing-S, -0.2px);
}

.promo-modal__copy {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.promo-modal__copy:hover {
  background: var(--light-grey-e-f);
}

.promo-modal__action-btn {
  width: 100%;
  font-size: var(--Size-M, 14px);
  line-height: var(--Line-Height-L, 20px);
  letter-spacing: var(--Letter-Spacing-S, -0.2px);
}
</style>
